import { isEqual } from "lodash";
import { observer } from "mobx-react-lite";
import { parse } from "query-string";
import { PropsWithChildren, useRef } from "react";

import { DateTime } from "@bps/date-time";
import { useStores } from "@stores/hooks/useStores.ts";
import { withFetch } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { FilterBar } from "@ui-components/filter-bar/FilterBar.tsx";

import { useClaimsFilterItems } from "./claims-filter-items.tsx";
import {
  ClaimsFilterQuery,
  ClaimsFilterState,
  defaultClaimsFilter
} from "./claims-filter.types.ts";

const TOOLTIP_TEXT =
  "Only showing claims with the status of accredited employer, lodge error, query error, declined, not verified, ready or incomplete. This will not show private claims";

const ClaimsFilterBase: React.FC<PropsWithChildren> = observer(
  ({ children }) => {
    const { routing } = useStores();

    const setFilter = (newFilter: Partial<ClaimsFilterState>) => {
      const existingFilter: ClaimsFilterState = parse(routing.location.search, {
        parseBooleans: true
      });

      const query: ClaimsFilterState = {
        ...existingFilter,
        ...newFilter
      };

      if (!isEqual(query, existingFilter)) {
        routing.pushQueryStringParams(query);
      }
    };

    const filterQuery: ClaimsFilterQuery = parse(routing.location.search, {
      parseBooleans: true
    });

    const convertStringToArray = (
      filterQuery: string[] | undefined | string
    ) => {
      if (!filterQuery) {
        return [];
      }
      if (Array.isArray(filterQuery)) {
        return filterQuery;
      }
      return [filterQuery];
    };

    const onFilterChange = (
      field: keyof ClaimsFilterState,
      values: ClaimsFilterState
    ) => {
      if (field === "dateTo" || field === "dateFrom") {
        const value = values[field];
        return setFilter({
          [field]: value ? DateTime.jsDateToISODate(value) : undefined
        });
      }

      return setFilter({ [field]: values[field] });
    };

    const filterItems = useClaimsFilterItems();

    const isFilterPristine = isEqual(filterQuery, defaultClaimsFilter);

    const initialValues = useRef<ClaimsFilterState>({
      patients: filterQuery.patients,
      claimStatusCode: convertStringToArray(filterQuery.claimStatusCode),
      claimNumber: filterQuery.claimNumber,
      insurers: convertStringToArray(filterQuery.insurers),
      claimIds: filterQuery.claimIds,
      providers: convertStringToArray(filterQuery.providers),
      dateTo: filterQuery.dateTo
        ? DateTime.jsDateFromISO(filterQuery.dateTo)
        : undefined,
      dateFrom: filterQuery.dateFrom
        ? DateTime.jsDateFromISO(filterQuery.dateFrom)
        : undefined,
      claimsActionRequired: filterQuery.claimsActionRequired
        ? filterQuery.claimsActionRequired
        : undefined,
      includePrivate: filterQuery.includePrivate
        ? filterQuery.includePrivate
        : true,
      includeDischarged: filterQuery.includeDischarged
        ? filterQuery.includeDischarged
        : false
    });
    return (
      <FilterBar<ClaimsFilterState>
        initialValues={initialValues.current}
        presets={[
          {
            text: "Include discharged",
            name: "includeDischarged",
            id: "claims-include-discharged",
            iconName: "Leave",
            tooltip: "Showing discharged claims",
            tooltipPresses: "Showing discharged claims"
          },
          {
            text: "Action required",
            name: "claimsActionRequired",
            id: "claims-action-required",
            iconName: "Warning",
            tooltip: TOOLTIP_TEXT,
            tooltipPresses: TOOLTIP_TEXT,
            valuesToBeSetOnToggleOn: {
              ...defaultClaimsFilter,
              claimsActionRequired: true
            },
            valuesToBeSetOnToggleOff: defaultClaimsFilter
          },
          {
            text: "Private",
            name: "includePrivate",
            id: "claims-include-private",
            iconName: "Lock",
            tooltip: "Exclude private",
            tooltipPresses: "Include private"
          }
        ]}
        onChange={({ field, state }) => onFilterChange(field, state.values)}
        customResetButtonCondition={() => isFilterPristine}
        items={filterItems}
        styles={{
          peoplePicker: { minWidth: 400 }
        }}
        onReset={(actions, values) => {
          if (!isEqual(defaultClaimsFilter, values)) {
            actions.reset(defaultClaimsFilter);
            routing.pushQueryStringParams(defaultClaimsFilter);
          }
        }}
      >
        {() => children}
      </FilterBar>
    );
  }
);

export const ClaimsFilter = withFetch(
  x => x.acc.ref.claimStatuses.load(),
  ClaimsFilterBase
);
